// spatialMap.js — Spatial coordinate mapping for Oscilla
// -----------------------------------------------------------
// Pure math module: SVG coordinates → spatial audio parameters
// No DOM dependencies. Consumed by o2p.js and controlXY.js (spatial mode).
//
// TWO PROJECTION MODES:
//
//   spatial:plan  — Top-down plan view (quad, stereo, flat arrays)
//     Center = listener position
//     Radius = distance from listener (0 = at listener, 1 = boundary)
//     Elevation via P handle (hmode) — 0–1 mapped to -90°..+90°
//
//   spatial:dome  — Polar azimuthal projection (hemisphere/dome arrays)
//     Center = zenith (directly above listener)
//     Edge   = horizon (ear level)
//     Radius = co-elevation (0 at center = 90°, 1 at edge = 0°)
//     Elevation is ENCODED in the radial position — no P handle needed
//     Distance = 1.0 (constant — dome speakers are equidistant)
//
// Coordinate conventions (both modes) -- the Ambisonics / IEM / ATK /
// SuperCollider VBAP convention:
//   Azimuth:   0° = front (top of the circle in the SVG), positive to the
//              LEFT (counter-clockwise seen from above), -180..180
//   Elevation: -90° (below) to +90° (above), 0° = horizon
//   Cartesian: x = front, y = left, z = up
// A renderer that counts clockwise (SPAT Revolution) takes -azimuth.
// -----------------------------------------------------------

// ============================================================================
//  MAIN DISPATCHER
// ============================================================================

/**
 * Map SVG coordinates to spatial audio parameters.
 * Dispatches to the correct projection based on mode.
 *
 * @param {string} mode       - "plan" | "dome" (default "plan")
 * @param {number} dx         - X offset from bounds center (SVG: right = +)
 * @param {number} dy         - Y offset from bounds center (SVG: down  = +)
 * @param {number} maxRadius  - Bounds radius in SVG units
 * @param {number} elevation  - Normalized elevation 0–1 (plan mode only)
 * @returns {SpatialPosition}
 */
export function mapToSpatial(mode, dx, dy, maxRadius, elevation = 0.5) {
    switch (mode) {
        case "dome":  return svgToSpatialDome(dx, dy, maxRadius);
        case "plan":
        default:      return svgToSpatialPlan(dx, dy, maxRadius, elevation);
    }
}


// ============================================================================
//  PLAN MODE — Top-down listener-centered view
// ============================================================================

/**
 * Plan projection: center = listener, radius = distance.
 * Elevation comes from the P handle (external, passed in as 0–1).
 *
 * @param {number} dx, dy     - offset from bounds center
 * @param {number} maxRadius  - bounds radius
 * @param {number} elevation  - normalized 0–1 (0.5 = horizon)
 * @returns {SpatialPosition}
 *
 * @typedef {Object} SpatialPosition
 * @property {number} azi      - Azimuth -180..180°, 0 front, + left
 * @property {number} elev     - Elevation -90° to +90°
 * @property {number} dist     - Distance 0–1
 * @property {number} x        - Cartesian X (-1 to 1)
 * @property {number} y        - Cartesian Y (-1 to 1)
 * @property {number} z        - Cartesian Z (-1 to 1)
 * @property {number} aziNorm  - Azimuth 0–1
 * @property {number} elevNorm - Elevation 0–1
 * @property {number} distNorm - Distance 0–1
 */
export function svgToSpatialPlan(dx, dy, maxRadius, elevation = 0.5) {
    const dist = Math.min(1, Math.sqrt(dx * dx + dy * dy) / (maxRadius || 1));

    const aziDeg = azimuthOf(dx, dy);

    // Elevation from P handle: 0 → -90°, 0.5 → 0°, 1 → +90°
    const elevDeg = (elevation - 0.5) * 180;

    return _buildResult(aziDeg, elevDeg, dist);
}


// ============================================================================
//  DOME MODE — Polar azimuthal (zenith-centered) projection
// ============================================================================

/**
 * Dome projection: center = zenith, edge = horizon.
 * Elevation is encoded in the radial position — no P handle needed.
 *
 * Drawing a path that spirals inward = source rising toward zenith.
 * Drawing a path on the outer ring = source moving at ear level.
 * Drawing through center = source passing directly overhead.
 *
 * Distance is fixed at 1.0 because dome speakers are equidistant
 * from the listener. The renderer distributes gain across speakers
 * based on source direction, not distance.
 *
 * @param {number} dx, dy     - offset from bounds center
 * @param {number} maxRadius  - bounds radius
 * @returns {SpatialPosition}
 */
export function svgToSpatialDome(dx, dy, maxRadius) {
    const aziDeg = azimuthOf(dx, dy);

    // Co-elevation: 0 at center (zenith), 1 at edge (horizon)
    const coElev = Math.min(1, Math.sqrt(dx * dx + dy * dy) / (maxRadius || 1));

    // Elevation: 90° at center, 0° at edge
    const elevDeg = 90 * (1 - coElev);

    // Distance: constant for dome arrays
    const dist = 1.0;

    return _buildResult(aziDeg, elevDeg, dist);
}


// ============================================================================
//  SHARED RESULT BUILDER
// ============================================================================

/**
 * Azimuth of an SVG offset from the centre: 0 at the top (front), positive
 * to the left, in (-180, 180]. SVG y points down, so front is -dy.
 */
export function azimuthOf(dx, dy) {
    if (dx === 0 && dy === 0) return 0;
    const deg = Math.atan2(-dx, -dy) * 180 / Math.PI;
    return deg === -180 ? 180 : deg + 0;                 // (+0: no -0 on the wire)
}

function _buildResult(aziDeg, elevDeg, dist) {
    const elevRad = elevDeg * Math.PI / 180;
    const aziRad  = aziDeg  * Math.PI / 180;
    const cosElev = Math.cos(elevRad);

    return {
        // AED — primary spatial representation
        azi:  aziDeg,
        elev: elevDeg,
        dist: dist,

        // Cartesian, x front, y left, z up (unit sphere when dist = 1)
        x: dist * Math.cos(aziRad) * cosElev,
        y: dist * Math.sin(aziRad) * cosElev,
        z: dist * Math.sin(elevRad),

        // Normalised 0..1 (for following with follow()): azimuth from front,
        // counter-clockwise (it wraps from 1 to 0 at the front); elevation
        // 0 = below, 0.5 = horizon, 1 = zenith, in both modes
        aziNorm:  ((aziDeg + 360) % 360) / 360,
        elevNorm: (elevDeg + 90) / 180,
        distNorm: dist
    };
}


// ============================================================================
//  BOUNDS RESOLUTION
// ============================================================================

/**
 * Extract center + radius from an SVG bounds element.
 * Supports <circle>, <ellipse>, <rect>, and generic elements (via bbox).
 */
export function resolveBounds(boundsEl) {
    if (!boundsEl) return { cx: 0, cy: 0, radius: 100 };

    const tag = boundsEl.tagName?.toLowerCase();

    if (tag === "circle") {
        return {
            cx:     parseFloat(boundsEl.getAttribute("cx")) || 0,
            cy:     parseFloat(boundsEl.getAttribute("cy")) || 0,
            radius: parseFloat(boundsEl.getAttribute("r"))  || 100
        };
    }

    if (tag === "ellipse") {
        const rx = parseFloat(boundsEl.getAttribute("rx")) || 100;
        const ry = parseFloat(boundsEl.getAttribute("ry")) || 100;
        return {
            cx:     parseFloat(boundsEl.getAttribute("cx")) || 0,
            cy:     parseFloat(boundsEl.getAttribute("cy")) || 0,
            radius: Math.min(rx, ry)
        };
    }

    const bbox = boundsEl.getBBox();
    return {
        cx:     bbox.x + bbox.width  / 2,
        cy:     bbox.y + bbox.height / 2,
        radius: Math.min(bbox.width, bbox.height) / 2
    };
}


// ============================================================================
//  OSC FORMAT BUILDERS
// ============================================================================

/**
 * Build an OSC args array in the format expected by a target renderer.
 *
 * Formats:
 *   aed      → [azi, elev, dist]                IEM, Spat Revolution, SpatGRIS
 *   xyz      → [x, y, z]                        ICST, generic cartesian
 *   ad       → [azi, dist]                       2D panning (no elevation)
 *   ae       → [azi, elev]                       direction only (no distance)
 *   aedg     → [azi, elev, dist, gain]           with distance-based gain
 *   spacemap → [x, y]                            Meyer Spacemap Go
 *   raw      → [aziNorm, elevNorm, distNorm]     all 0–1, for custom routing
 */
export function buildOSCArgs(sp, format = "aed", opts = {}) {
    const { gain = 1.0 } = opts;

    switch (format) {
        case "aed":      return [sp.azi, sp.elev, sp.dist];
        case "xyz":      return [sp.x, sp.y, sp.z];
        case "ad":       return [sp.azi, sp.dist];
        case "ae":       return [sp.azi, sp.elev];
        case "aedg":     return [sp.azi, sp.elev, sp.dist, gain * (1 - sp.dist * 0.5)];
        case "spacemap": return [sp.x, sp.y];
        case "raw":      return [sp.aziNorm, sp.elevNorm, sp.distNorm];
        default:         return [sp.azi, sp.elev, sp.dist];
    }
}


// ============================================================================
//  INTERPOLATION
// ============================================================================

/** Any angle in degrees -> (-180, 180]. */
export function wrapAzimuth(deg) {
    const w = ((deg % 360) + 540) % 360 - 180;
    return w === -180 ? 180 : w + 0;
}

/** Shortest-arc azimuth lerp. Handles the 179° -> -179° wrap. */
export function lerpAzimuth(a, b, t) {
    let diff = b - a;
    if (diff >  180) diff -= 360;
    if (diff < -180) diff += 360;
    return wrapAzimuth(a + diff * t);
}

/** Interpolate two spatial positions (for preset tweening). */
export function lerpSpatial(posA, posB, t) {
    return {
        azi:  lerpAzimuth(posA.azi, posB.azi, t),
        elev: posA.elev + (posB.elev - posA.elev) * t,
        dist: posA.dist + (posB.dist - posA.dist) * t
    };
}


// ============================================================================
//  PARAMETRIC TRAJECTORY GENERATORS
// ============================================================================

/** Circular orbit at constant elevation. */
export function circularOrbit(radius = 0.7, elevation = 0.5, revolutions = 1) {
    return (t) => ({
        azi:  wrapAzimuth(t * 360 * revolutions),
        elev: (elevation - 0.5) * 180,
        dist: radius
    });
}

/** Spiral — radius changes as azimuth advances. */
export function spiral(startR = 0.1, endR = 0.9, elevation = 0.5, revolutions = 3) {
    return (t) => ({
        azi:  wrapAzimuth(t * 360 * revolutions),
        elev: (elevation - 0.5) * 180,
        dist: startR + (endR - startR) * t
    });
}

/** Lissajous — complex orbital pattern. */
export function lissajous(a = 3, b = 2, delta = Math.PI / 2, radius = 0.7) {
    return (t) => {
        const theta = t * 2 * Math.PI;
        const lx = Math.sin(a * theta + delta);
        const ly = Math.sin(b * theta);
        return {
            azi:  wrapAzimuth(Math.atan2(lx, ly) * 180 / Math.PI),
            elev: 0,
            dist: radius * Math.sqrt(lx * lx + ly * ly) / Math.SQRT2
        };
    };
}

/** Pendulum — swings between two azimuth angles. */
export function pendulum(aziA = 45, aziB = -45, radius = 0.6, elevation = 0.5) {
    return (t) => ({
        azi:  lerpAzimuth(aziA, aziB, 0.5 - 0.5 * Math.cos(t * Math.PI)),
        elev: (elevation - 0.5) * 180,
        dist: radius
    });
}

/** Random walk — Brownian motion in AED space (seeded). */
export function randomWalk(stepSize = 5, radius = 0.5, seed = 42) {
    let s = seed;
    const rand = () => { s = (s * 1664525 + 1013904223) & 0xFFFFFFFF; return (s >>> 0) / 0xFFFFFFFF; };

    const positions = [{ azi: 0, elev: 0, dist: radius }];
    for (let i = 1; i <= 200; i++) {
        const prev = positions[i - 1];
        positions.push({
            azi:  wrapAzimuth(prev.azi + (rand() - 0.5) * stepSize * 2),
            elev: Math.max(-90, Math.min(90, prev.elev + (rand() - 0.5) * stepSize)),
            dist: Math.max(0.05, Math.min(1, prev.dist + (rand() - 0.5) * stepSize * 0.02))
        });
    }

    return (t) => {
        const idx = t * (positions.length - 1);
        const lo = Math.floor(idx);
        const hi = Math.min(lo + 1, positions.length - 1);
        const frac = idx - lo;
        return lerpSpatial(positions[lo], positions[hi], frac);
    };
}

// --- Dome-specific generators ---

/**
 * Zenith dive — source descends from zenith to horizon and back.
 * In dome SVG: spirals outward from center to edge and back.
 * @param {number} azimuth - fixed azimuth in degrees
 */
export function zenithDive(azimuth = 0, cycles = 1) {
    return (t) => {
        const phase = 0.5 - 0.5 * Math.cos(t * 2 * Math.PI * cycles);
        return {
            azi: azimuth,
            elev: 90 * (1 - phase),  // 90° at t=0, 0° at midpoint, 90° at end
            dist: 1.0
        };
    };
}

/**
 * Hemisphere sweep — horizon to horizon over the zenith, along the great
 * circle through `startAzi` and its opposite (in the dome SVG: a diameter).
 * Each pass goes over and back down the far side; `passes` of them.
 * @param {number} startAzi - where it rises, in degrees
 */
export function hemisphereSweep(startAzi = 0, passes = 1) {
    return (t) => {
        // angle along the great circle: 0 at startAzi's horizon, 180 at the opposite one
        const along = ((t * passes) % 1) * 180;
        return {
            azi:  wrapAzimuth(along <= 90 ? startAzi : startAzi + 180),
            elev: along <= 90 ? along : 180 - along,
            dist: 1.0
        };
    };
}

/**
 * Dome spiral — spirals from horizon to zenith (or reverse).
 * In dome SVG: visible spiral from edge to center.
 */
export function domeSpiral(revolutions = 3, ascending = true) {
    return (t) => {
        const dir = ascending ? t : (1 - t);
        return {
            azi: wrapAzimuth(t * 360 * revolutions),
            elev: dir * 90,       // 0° → 90° (or reverse)
            dist: 1.0
        };
    };
}


// ============================================================================
//  SVG PATH GENERATION
// ============================================================================

/**
 * Convert a parametric generator to an SVG path string.
 *
 * @param {Function} generator - (t) → { azi, elev, dist }
 * @param {{ cx, cy, radius }} bounds
 * @param {string} mode - "plan" | "dome" (affects coordinate inversion)
 * @param {number} steps
 * @returns {string} SVG path d-attribute
 */
export function generatorToSVGPath(generator, bounds, mode = "plan", steps = 100) {
    const parts = [];

    for (let i = 0; i <= steps; i++) {
        const t   = i / steps;
        const pos = generator(t);

        const aziRad = pos.azi * Math.PI / 180;
        let r, svgX, svgY;

        if (mode === "dome") {
            // Dome: radius = co-elevation (0 at zenith/center, 1 at horizon/edge)
            const coElev = 1 - (pos.elev / 90);
            r    = coElev * bounds.radius;
        } else {
            // Plan: radius = distance
            r    = pos.dist * bounds.radius;
        }
        // the inverse of azimuthOf: front is up, positive azimuth to the left
        svgX = bounds.cx - r * Math.sin(aziRad);
        svgY = bounds.cy - r * Math.cos(aziRad);

        parts.push(`${i === 0 ? "M" : "L"} ${svgX.toFixed(2)} ${svgY.toFixed(2)}`);
    }

    return parts.join(" ");
}


// ============================================================================
//  OVERLAY TEXT FORMATTER
// ============================================================================

export function formatSpatialOverlay(sp, format) {
    switch (format) {
        case "aed":
        case "aedg":
            return `az:${sp.azi.toFixed(1)}° el:${sp.elev.toFixed(1)}° d:${sp.dist.toFixed(2)}`;
        case "xyz":
            return `x:${sp.x.toFixed(2)} y:${sp.y.toFixed(2)} z:${sp.z.toFixed(2)}`;
        case "ad":
            return `az:${sp.azi.toFixed(1)}° d:${sp.dist.toFixed(2)}`;
        case "ae":
            return `az:${sp.azi.toFixed(1)}° el:${sp.elev.toFixed(1)}°`;
        default:
            return `az:${sp.azi.toFixed(1)}° el:${sp.elev.toFixed(1)}°`;
    }
}


// ============================================================================
//  WINDOW BINDING
// ============================================================================

window.oscillaSpatial = {
    mapToSpatial,
    svgToSpatialPlan,
    svgToSpatialDome,
    resolveBounds,
    buildOSCArgs,
    azimuthOf,
    wrapAzimuth,
    lerpAzimuth,
    lerpSpatial,
    circularOrbit,
    spiral,
    lissajous,
    pendulum,
    randomWalk,
    zenithDive,
    hemisphereSweep,
    domeSpiral,
    generatorToSVGPath,
    formatSpatialOverlay
};
